import { Loader } from "lucide-react";
import React, { useEffect, useState } from "react";
import ImagePreviewModal from "./image-preview-modal.tsx";

// Updated interface to track different data sources
interface DetectionRecord {
  gateLocation: any;
  id: string | number;
  timestampEntry: string;
  timestampExit?: string;
  originalImageEntry?: string;
  plateImageEntry?: string;
  vehicleImageEntry?: string;
  originalImageExit?: string;
  plateImageExit?: string;
  annotatedImageEntry?: string;
  annotatedImageExit?: string;
  plateNumber: string;
  vehicleType: string;
  region: string;
  plateType: string;
  confidence: number | null;
  detection_details?: any;
  detection_type?: string;
  file_path?: string;
  original_path?: string;
  annotated_path?: string;
  plateStatus: "blacklist" | "whitelist" | "unclassified" | null;  // Improve typing
  processing_time?: string;
  image_resolution?: string;
}

interface EditDetailModalProps {
  isOpen: boolean;
  record: DetectionRecord | null;
  onClose: () => void;
  onUpdate?: (updatedRecord: any) => void;  // Callback to notify parent of updates
}

const EditDetailModal: React.FC<EditDetailModalProps> = ({
  isOpen,
  record,
  onClose,
  onUpdate,
}) => {
  const [tab, setTab] = useState<"entry" | "exit">("entry");
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plateDetails, setPlateDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasExitData, setHasExitData] = useState(false);

  // Add state for image preview modal
  const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
  const [showPreview, setShowPreview] = useState(false);

  // Restructure form to separate fields by their source
  const [form, setForm] = useState({
    // Plate-related fields
    plate: {
      plateNumber: "",
      plateDate: "",
      plateType: "N/A",
      plateStatus: "N/A", // Changed from listType to plateStatus
      plateTextColor: "",
      plateBackgroundColor: "",
      plateRegionCode: "",
      plateRegionName: "",
      plateRegion: "",
      plateIcon: "",
      plateIconColor: "",
      plateBlueStrip: "no"
    },
    // Vehicle-related fields
    vehicle: {
      vehicleType: "",
    },
    // Session-related fields
    session: {
      location: "",
      imageResolution: "",
      processingTime: "",
    }
  });

  // Track which entity data has been modified to determine which APIs to call
  const [modifiedEntities, setModifiedEntities] = useState({
    plate: false,
    vehicle: false,
    session: false
  });

  // Track separate IDs for each entity
  const [entityIds, setEntityIds] = useState({
    plateId: null,
    vehicleId: null,
    sessionId: null
  });

  // Add state for available locations
  const [availableLocations, setAvailableLocations] = useState<Array<{ id: number, name: string, type: string }>>([]);

  // Fetch full plate details when modal opens
  useEffect(() => {
    if (isOpen && record && record.id) {
      const fetchPlateDetails = async () => {
        setLoading(true);
        setError(null);

        try {
          const plateId = record.detection_details?.id || record.id;
          const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/plates/${plateId}`);

          if (!response.ok) {
            throw new Error(`Error fetching plate details: ${response.statusText}`);
          }

          const data = await response.json();
          setPlateDetails(data);

          // Extract entity IDs from the data
          setEntityIds({
            plateId: data.id || plateId,
            vehicleId: data.vehicle_id || record.detection_details?.vehicle_id,
            sessionId: data.session_id || record.detection_details?.session_id
          });

          // Initialize form with fetched data
          setForm({
            plate: {
              plateNumber: data.plate_text || record.plateNumber || "",
              plateDate: data.plate_date || record.detection_details?.plate_date || "",
              plateType: (data.plate_type && data.plate_type !== "") ? data.plate_type :
                (record.plateType && record.plateType !== "") ? record.plateType : "N/A",
              plateStatus: (data.plate_status && data.plate_status !== "") ? data.plate_status :
                record.plateStatus ? record.plateStatus : "N/A",
              plateTextColor: data.plate_text_color || record.detection_details?.plate_text_color || "",
              plateBackgroundColor: data.plate_background_color || record.detection_details?.plate_background_color || "",
              plateRegionCode: data.plate_region_code || "",
              plateRegionName: data.plate_region_name || "",
              plateRegion: data.plate_region || record.region || "",
              plateIcon: data.plate_icon || "",
              plateIconColor: data.plate_icon_color || "",
              plateBlueStrip: data.plate_blue_strip ? "yes" : "no"
            },
            vehicle: {
              vehicleType: data.vehicle_class || record.vehicleType || "",
            },
            session: {
              location: data.location || record.detection_details?.location || "",
              imageResolution: record.image_resolution || record.detection_details?.image_resolution || "",
              processingTime: record.processing_time || record.detection_details?.processing_time || "",
            }
          });

          // Map API image paths to expected frontend field names
          if (data) {
            record.originalImageEntry = data.stored_original_path;
            record.plateImageEntry = data.plate_image_path;
            record.annotatedImageEntry = data.vehicle_annotated_path;
            record.vehicleImageEntry = data.vehicle_image_path || data.vehicle_crop_path;
          }

          // Get the correct vehicle ID
          const vehicleId = data.vehicle_id || record.detection_details?.vehicle_id;

          // If we have a vehicleId, fetch the vehicle details to get the correct session ID
          if (vehicleId) {
            try {
              const vehicleResponse = await fetch(`https://galaxy-alpr-be.onrender.com/api/vehicles/${vehicleId}`);

              if (!vehicleResponse.ok) {
                throw new Error(`Error fetching vehicle details: ${vehicleResponse.statusText}`);
              }

              const vehicleData = await vehicleResponse.json();
              const sessionId = vehicleData.vehicle?.session_id || vehicleData.session_id;

              // Update entityIds with the correct session ID from the vehicle data
              setEntityIds(prev => ({
                ...prev,
                plateId: data.id || plateId,
                vehicleId: vehicleId,
                sessionId: sessionId
              }));

              console.log("Retrieved session ID from vehicle:", sessionId);

              // If we have a session ID, fetch the session to get the location_id
              if (sessionId) {
                try {
                  // First fetch the session to get the location_id
                  const sessionResponse = await fetch(`https://galaxy-alpr-be.onrender.com/api/sessions/${sessionId}`);
                  if (sessionResponse.ok) {
                    const sessionData = await sessionResponse.json();
                    const locationId = sessionData.session?.location_id || sessionData.location_id;

                    if (locationId) {
                      // Fetch specific location using location_id
                      const locationResponse = await fetch(`https://galaxy-alpr-be.onrender.com/api/locations/${locationId}`);
                      if (locationResponse.ok) {
                        const locationData = await locationResponse.json();

                        // Update form with the correct location
                        setForm(prev => ({
                          ...prev,
                          session: {
                            ...prev.session,
                            location: locationData.name || prev.session.location
                          }
                        }));
                      }
                    }

                    // Still fetch all locations for the dropdown in edit mode
                    const locationsResponse = await fetch("https://galaxy-alpr-be.onrender.com/api/locations");
                    if (locationsResponse.ok) {
                      const locationsData = await locationsResponse.json();
                      setAvailableLocations(locationsData || []);
                    }
                  }
                } catch (sessionErr) {
                  console.error("Error fetching session or location details:", sessionErr);
                }
              }

            } catch (vehicleErr) {
              console.error("Error fetching vehicle details:", vehicleErr);
              // Fallback to existing logic if vehicle fetch fails
              setEntityIds({
                plateId: data.id || plateId,
                vehicleId: vehicleId,
                sessionId: data.session_id || record.detection_details?.session_id
              });
            }
          } else {
            // Fallback if no vehicle ID is available
            setEntityIds({
              plateId: data.id || plateId,
              vehicleId: vehicleId,
              sessionId: data.session_id || record.detection_details?.session_id
            });
          }

        } catch (err) {
          console.error("Error fetching plate details:", err);
          setError(err instanceof Error ? err.message : "An error occurred");

          // Fall back to the record data we already have
          setForm({
            plate: {
              plateNumber: record.plateNumber || "",
              plateDate: record.detection_details?.plate_date || "",
              plateType: record.plateType || "N/A",
              plateStatus: record.plateStatus || "N/A", // Changed to plateStatus
              plateTextColor: record.detection_details?.plate_text_color || "",
              plateBackgroundColor: record.detection_details?.plate_background_color || "",
              plateRegionCode: "",
              plateRegionName: "",
              plateRegion: record.region || "",
              plateIcon: "",
              plateIconColor: "",
              plateBlueStrip: "no"
            },
            vehicle: {
              vehicleType: record.vehicleType || "",
            },
            session: {
              location: record.detection_details?.location || "",
              imageResolution: record.image_resolution || record.detection_details?.image_resolution || "",
              processingTime: record.processing_time || record.detection_details?.processing_time || "",
            }
          });
        } finally {
          setLoading(false);
        }
      };

      fetchPlateDetails();
    }
  }, [isOpen, record]);

  // Check if exit data exists when record changes
  useEffect(() => {
    if (record) {
      // Check if we have any exit data to display
      const hasExit = !!(
        record.timestampExit &&
        (record.originalImageExit ||
          record.plateImageExit ||
          record.annotatedImageExit)
      );
      setHasExitData(hasExit);

      // If we switched to a record with no exit data, ensure we're on entry tab
      if (!hasExit && tab === "exit") {
        setTab("entry");
      }
    }
  }, [record, tab]);

  if (!isOpen || !record) return null;

  const getPlateTypeColor = (plateType: string) => {
    switch (plateType.toLowerCase()) {
      case "military":
        return "bg-green-600 text-white";
      case "private":
        return "bg-cyan-500 text-white";
      case "public":
        return "bg-blue-500 text-white";
      case "government":
        return "bg-purple-500 text-white";
      case "police":
        return "bg-indigo-600 text-white";
      case "diplomatic":
        return "bg-yellow-600 text-white";
      case "free_trade":
        return "bg-orange-500 text-white";
      case "regular":
        return "bg-cyan-500 text-white"; // Keep backward compatibility with "Regular"
      case "blacklist":
        return "bg-red-500 text-white";
      case "whitelist":
        return "bg-green-500 text-white";
      case "unclassified":
        return "bg-gray-400 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return "text-green-600";
    if (confidence >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  // Helper function to handle image URLs - Updated to handle annotated images
  const getImageUrl = (url: string | undefined, type: string = ''): string | undefined => {
    if (!url) return undefined;

    // If it's already a full URL, return it
    if (url.startsWith('http')) return url;

    // Make sure paths that start with /outputs go directly to outputs endpoint
    if (url.startsWith('/outputs')) {
      return `https://galaxy-alpr-be.onrender.com${url}`;
    }

    // Special handling for annotated images
    if (type === 'annotated') {
      // Most annotated images will be in the root vehicles or plates folder
      if (url.includes('vehicles')) {
        const filename = url.split('/').pop();
        return `https://galaxy-alpr-be.onrender.com/outputs/vehicles/${filename}`;
      } else if (url.includes('plates')) {
        const filename = url.split('/').pop();
        return `https://galaxy-alpr-be.onrender.com/outputs/plates/${filename}`;
      }
    }

    // Handle paths that need the uploads endpoint
    if (url.startsWith('/uploads') || type === 'original' || type === 'uploaded') {
      // Extract filename from the path to avoid duplicating path segments
      const filename = url.split('/').pop();
      return `https://galaxy-alpr-be.onrender.com/uploads/${filename}`;
    }

    // Legacy handling - if path doesn't have expected format but contains certain directories
    if (url.includes('/vehicles/') || url.includes('/plates/')) {
      const parts = url.split('/');
      const directory = parts.includes('vehicles') ? 'vehicles' : 'plates';
      const subdir = parts.includes('crops') ? 'crops' : '';
      const filename = parts.pop();

      if (subdir) {
        return `https://galaxy-alpr-be.onrender.com/outputs/${directory}/${subdir}/${filename}`;
      }
      return `https://galaxy-alpr-be.onrender.com/outputs/${directory}/${filename}`;
    }

    // Default fallback - add the host prefix
    return `https://galaxy-alpr-be.onrender.com/${url}`;
  };

  // Get image sources with prioritized fallbacks - completely revised logic
  const getImageSources = (isEntryTab: boolean) => {
    if (isEntryTab) {
      // Entry images - include all four required image types with correct source prioritization
      return {
        originalImage: getImageUrl(
          record.originalImageEntry ||
          plateDetails?.original_image_path ||
          plateDetails?.stored_original_path
        ),
        croppedPlateImage: getImageUrl(
          record.plateImageEntry ||
          plateDetails?.plate_image_path ||
          plateDetails?.file_path
        ),
        croppedVehicleImage: getImageUrl(
          record.vehicleImageEntry ||
          plateDetails?.vehicle_image_path ||
          plateDetails?.vehicle_crop_path
        ),
        annotatedPlateImage: getImageUrl(
          record.annotated_path ||
          plateDetails?.plate_annotated_path
        ),
        annotatedVehicleImage: getImageUrl(
          record.annotatedImageEntry ||
          plateDetails?.vehicle_annotated_path
        ),
      };
    } else {
      // Exit tab - ensure we return all properties including croppedVehicleImage
      return {
        originalImage: null,
        croppedPlateImage: null,
        croppedVehicleImage: null,
        annotatedPlateImage: null,
        annotatedVehicleImage: null,
      };
    }
  };

  // Get image sources based on current tab
  const imageSources = getImageSources(tab === "entry");

  // Tab content for current view
  const details = {
    timestamp: tab === "entry" ? record.timestampEntry : record.timestampExit,
    originalImage: imageSources.originalImage,
    croppedPlateImage: imageSources.croppedPlateImage,
    croppedVehicleImage: imageSources.croppedVehicleImage,
    annotatedPlateImage: imageSources.annotatedPlateImage,
    annotatedVehicleImage: imageSources.annotatedVehicleImage,
    label: tab === "entry" ? "Entry" : "Exit",
    available: tab === "entry" || hasExitData,
  };

  // Debug images
  console.log("Image paths:", {
    tab,
    originalImageEntry: record.originalImageEntry,
    plateImageEntry: record.plateImageEntry,
    vehicleImageEntry: record.vehicleImageEntry, // Log the vehicle image entry
    annotatedImageEntry: record.annotatedImageEntry,
    originalImage: details.originalImage,
    croppedPlateImage: details.croppedPlateImage,
    croppedVehicleImage: details.croppedVehicleImage,
    annotatedPlateImage: details.annotatedPlateImage,
    annotatedVehicleImage: details.annotatedVehicleImage,
    plateDetailsImages: plateDetails ? {
      original_image_path: plateDetails.original_image_path,
      stored_original_path: plateDetails.stored_original_path,
      plate_image_path: plateDetails.plate_image_path,
      vehicle_image_path: plateDetails.vehicle_image_path, // Add vehicle image path
      vehicle_crop_path: plateDetails.vehicle_crop_path,   // Add vehicle crop path
      file_path: plateDetails.file_path,
      plate_annotated_path: plateDetails.plate_annotated_path,
      vehicle_annotated_path: plateDetails.vehicle_annotated_path
    } : null
  });

  // Fix handleChange to properly identify session and vehicle fields
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Determine which entity this field belongs to
    let entityType: 'plate' | 'vehicle' | 'session' = 'plate';
    let fieldName = name;

    // Parse field name to determine entity type and extract the actual field name
    if (name.includes('.')) {
      const [entity, field] = name.split('.');
      entityType = entity as 'plate' | 'vehicle' | 'session';
      fieldName = field;
    }
    // Legacy checks for backwards compatibility
    else if (name.startsWith('vehicle')) {
      entityType = 'vehicle';
    } else if (name.startsWith('session')) {
      entityType = 'session';
    }

    console.log(`Field change: Entity=${entityType}, Field=${fieldName}, Value=${value}`);

    // Update the form state with the correct entity and field name
    setForm(prev => ({
      ...prev,
      [entityType]: {
        ...prev[entityType],
        [fieldName]: value
      }
    }));

    // Mark this entity as modified
    setModifiedEntities(prev => ({
      ...prev,
      [entityType]: true
    }));
  };

  // Handle updating plate data
  const updatePlateData = async () => {
    if (!entityIds.plateId) {
      return { success: false, error: "Missing plate ID" };
    }

    console.log(`Updating plate with ID: ${entityIds.plateId}`);

    try {
      // Only include fields that have actually changed
      const originalPlate = plateDetails || {};
      const updateData: Record<string, any> = {};

      // Compare current form values with original values and only include changed fields
      if (form.plate.plateNumber !== originalPlate.plate_text)
        updateData.plate_text = form.plate.plateNumber;

      if (form.plate.plateType !== originalPlate.plate_type)
        updateData.plate_type = form.plate.plateType;

      if (form.plate.plateDate !== originalPlate.plate_date)
        updateData.plate_date = form.plate.plateDate;

      // Add missing check for plateStatus field
      if (form.plate.plateStatus !== originalPlate.plate_status)
        updateData.plate_status = form.plate.plateStatus;

      if (form.plate.plateTextColor !== originalPlate.plate_text_color)
        updateData.plate_text_color = form.plate.plateTextColor;

      if (form.plate.plateBackgroundColor !== originalPlate.plate_background_color)
        updateData.plate_background_color = form.plate.plateBackgroundColor;

      if (form.plate.plateRegionCode !== originalPlate.plate_region_code)
        updateData.plate_region_code = form.plate.plateRegionCode;

      if (form.plate.plateRegionName !== originalPlate.plate_region_name)
        updateData.plate_region_name = form.plate.plateRegionName;

      if (form.plate.plateRegion !== originalPlate.plate_region)
        updateData.plate_region = form.plate.plateRegion;

      if (form.plate.plateIcon !== originalPlate.plate_icon)
        updateData.plate_icon = form.plate.plateIcon;

      if (form.plate.plateIconColor !== originalPlate.plate_icon_color)
        updateData.plate_icon_color = form.plate.plateIconColor;

      const blueStrip = form.plate.plateBlueStrip === "yes";
      if (blueStrip !== !!originalPlate.plate_blue_strip)
        updateData.plate_blue_strip = blueStrip;

      // If no fields changed, don't make the API call
      if (Object.keys(updateData).length === 0) {
        console.log("No changes detected for plate data");
        return { success: true, data: originalPlate };
      }

      console.log(`Sending plate update data (${Object.keys(updateData).length} changed fields):`, updateData);

      const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/plates/${entityIds.plateId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`Error updating plate: ${response.statusText}`);
      }

      const result = await response.json();
      console.log("Plate update successful:", result);
      return { success: true, data: result.plate };

    } catch (err) {
      console.error("Error updating plate:", err);
      return { success: false, error: err instanceof Error ? err.message : "Failed to update plate" };
    }
  };

  // Handle updating vehicle data
  const updateVehicleData = async () => {
    if (!entityIds.vehicleId) {
      return { success: false, error: "Missing vehicle ID" };
    }

    console.log(`Updating vehicle with ID: ${entityIds.vehicleId}`);

    try {
      const updateData = {
        vehicle_class: form.vehicle.vehicleType,
        // Removed: vehicle_color, vehicle_make, vehicle_model fields
        // These fields are not supported by the backend API
      };

      console.log(`Sending vehicle update data:`, updateData);

      const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/vehicles/${entityIds.vehicleId}`, {
        method: "PUT", // Changed to double quotes
        headers: {
          "Content-Type": "application/json", // Changed to double quotes
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`Error updating vehicle: ${response.statusText}`);
      }

      const result = await response.json();
      console.log("Vehicle update successful:", result);
      return { success: true, data: result.vehicle };

    } catch (err) {
      console.error("Error updating vehicle:", err);
      return { success: false, error: err instanceof Error ? err.message : "Failed to update vehicle" };
    }
  };

  // Handle updating session data
  const updateSessionData = async () => {
    if (!entityIds.sessionId) {
      return { success: false, error: "Missing session ID" };
    }

    console.log(`Updating session with ID: ${entityIds.sessionId}`);

    try {
      const updateData = {
        location: form.session.location,
        image_resolution: form.session.imageResolution,
      };

      console.log(`Sending session update data:`, updateData);

      // Use the new direct session endpoint
      const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/sessions/${entityIds.sessionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`Error updating session: ${response.statusText}`);
      }

      const result = await response.json();
      console.log("Session update successful:", result);
      return { success: true, data: result.session };

    } catch (err) {
      console.error("Error updating session:", err);
      return { success: false, error: err instanceof Error ? err.message : "Failed to update session" };
    }
  };

  // Add a refreshDetails function to fetch updated data
  const refreshDetails = async () => {
    if (!entityIds.plateId) return;

    setLoading(true);
    try {
      const plateId = entityIds.plateId;
      const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/plates/${plateId}`);

      if (!response.ok) {
        throw new Error(`Error fetching plate details: ${response.statusText}`);
      }

      const data = await response.json();
      setPlateDetails(data);

      // Update form with latest data
      setForm(prev => ({
        ...prev,
        plate: {
          ...prev.plate,
          plateNumber: data.plate_text || prev.plate.plateNumber,
          plateType: (data.plate_type && data.plate_type !== "") ? data.plate_type :
            (record.plateType && record.plateType !== "") ? record.plateType : "N/A",
          plateDate: data.plate_date || prev.plate.plateDate,
          plateStatus: (data.plate_status && data.plate_status !== "") ? data.plate_status :
            (record.plateStatus) ? record.plateStatus : "N/A",
          plateTextColor: data.plate_text_color || prev.plate.plateTextColor,
          plateBackgroundColor: data.plate_background_color || prev.plate.plateBackgroundColor,
          plateRegionCode: data.plate_region_code || prev.plate.plateRegionCode,
          plateRegionName: data.plate_region_name || prev.plate.plateRegionName,
          plateRegion: data.plate_region || record.region || prev.plate.plateRegion,
          plateIcon: data.plate_icon || prev.plate.plateIcon,
          plateIconColor: data.plate_icon_color || prev.plate.plateIconColor,
          plateBlueStrip: data.plate_blue_strip ? "yes" : "no"
        },
        vehicle: {
          ...prev.vehicle,
          vehicleType: data.vehicle_class || prev.vehicle.vehicleType,
        },
        session: {
          ...prev.session,
          location: data.location || prev.session.location,
          imageResolution: data.image_resolution || prev.session.imageResolution,
          processingTime: prev.session.processingTime,
        }
      }));

    } catch (err) {
      console.error("Error refreshing details:", err);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced save handler that calls appropriate update functions
  const handleSave = async () => {
    setSaving(true);
    setError(null);

    const results = {
      plate: { success: false, message: "", attempted: false },
      vehicle: { success: false, message: "", attempted: false },
      session: { success: false, message: "", attempted: false }
    };

    try {
      // Update plate data if modified
      if (modifiedEntities.plate) {
        results.plate.attempted = true;
        const plateResult = await updatePlateData();
        results.plate.success = plateResult.success;
        results.plate.message = plateResult.success ? "Plate updated successfully" : (plateResult.error ?? "Unknown error");

        if (plateResult.success) {
          // Update local state with new plate data
          setPlateDetails(prev => ({ ...prev, ...plateResult.data }));
        }
      }

      // Update vehicle data if modified
      if (modifiedEntities.vehicle) {
        results.vehicle.attempted = true;
        const vehicleResult = await updateVehicleData();
        results.vehicle.success = vehicleResult.success;
        results.vehicle.message = vehicleResult.success ? "Vehicle updated successfully" : (vehicleResult.error ?? "Unknown error");
      }

      // Update session data if modified
      if (modifiedEntities.session) {
        results.session.attempted = true;
        const sessionResult = await updateSessionData();
        results.session.success = sessionResult.success;
        results.session.message = sessionResult.success ? "Session updated successfully" : (sessionResult.error ?? "Unknown error");
      }

      // Check if any updates were attempted and failed
      const hasFailures = Object.values(results).some(r => r.attempted && !r.success);

      if (hasFailures) {
        // Collect error messages from failed updates
        const errorMessages = Object.entries(results)
          .filter(([_, result]) => result.attempted && !result.success)
          .map(([entity, result]) => `${entity}: ${result.message}`);

        setError(`Some updates failed: ${errorMessages.join(', ')}`);
      } else {
        // Reset modified flags if all updates succeeded
        setModifiedEntities({ plate: false, vehicle: false, session: false });

        // Refresh the details to show updated data
        await refreshDetails();

        // Exit edit mode
        setEditMode(false);

        // Notify parent component of successful updates
        if (onUpdate) {
          onUpdate({
            plateUpdated: results.plate.attempted && results.plate.success,
            vehicleUpdated: results.vehicle.attempted && results.vehicle.success,
            sessionUpdated: results.session.attempted && results.session.success,
          });
        }
      }
    } catch (err) {
      console.error("Error during update process:", err);
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  // Function to get display values, preferring plateDetails over record
  const getDisplayValue = (key: string, fallback: string = "N/A") => {
    if (loading) return "Loading...";

    if (plateDetails && plateDetails[key]) {
      return plateDetails[key];
    }

    if (record.detection_details && record.detection_details[key]) {
      return record.detection_details[key];
    }

    if (record[key]) {
      return record[key];
    }

    return fallback;
  };

  // Helper function to handle image clicks
  const handleImageClick = (imageUrl: string | undefined) => {
    if (imageUrl) {
      setPreviewImage(imageUrl);
      setShowPreview(true);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 overflow-hidden">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 animate-pop-in relative max-h-[90vh] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 bg-gray-200 hover:bg-gray-300 rounded-full p-2 z-10"
          aria-label="Close"
        >
          <span className="text-xl font-bold text-gray-700">&times;</span>
        </button>

        {/* Header section - fixed */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">
            {record.plateNumber} Details
            {loading && <Loader className="ml-2 inline animate-spin" size={16} />}
          </h2>
          <div className="mt-4 flex">
            <button
              className={`px-6 py-2 text-sm font-medium focus:outline-none ${tab === "entry"
                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                : "text-gray-500 hover:text-blue-600"
                }`}
              style={{
                background: tab === "entry" ? "#f8fbff" : "transparent",
              }}
              onClick={() => setTab("entry")}
            >
              Entry
            </button>
            <button
              className={`px-6 py-2 text-sm font-medium focus:outline-none ${!hasExitData
                ? "text-gray-400 cursor-not-allowed"
                : tab === "exit"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                  : "text-gray-500 hover:text-blue-600"
                }`}
              style={{
                background: tab === "exit" ? "#f8fbff" : "transparent",
                opacity: !hasExitData ? 0.6 : 1
              }}
              onClick={() => hasExitData && setTab("exit")}
              disabled={!hasExitData}
            >
              Exit {!hasExitData && "(Not Available)"}
            </button>
          </div>
        </div>

        {/* Scrollable content section */}
        <div className="p-6 overflow-y-auto flex-grow">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left: Images section - No changes needed */}
            <div className="space-y-4">
              {tab === "exit" ? (
                // Exit tab - no images, just a message
                <div className="w-full h-60 flex items-center justify-center bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <span className="text-sm text-gray-500">No images available for exit data</span>
                    <p className="mt-2 text-xs text-gray-400">Exit images are not captured in this version.</p>
                  </div>
                </div>
              ) : (
                // Entry tab - All four image types
                <>
                  {/* Original Image */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Original Image</h3>
                    {loading ? (
                      <div className="w-full h-32 flex items-center justify-center bg-gray-100 rounded-lg">
                        <Loader className="animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <img
                        src={
                          details.originalImage ||
                          "https://via.placeholder.com/300x200?text=No+Original+Image"
                        }
                        alt="Entry scene"
                        className="w-full h-32 object-cover rounded-lg shadow cursor-pointer"
                        onClick={() => handleImageClick(details.originalImage || undefined)}
                        onError={(e) => {
                          console.log("Original image failed to load:", e.currentTarget.src);
                          console.log("Original image path:", details.originalImage);
                          e.currentTarget.src = "https://via.placeholder.com/300x200?text=Image+Load+Failed";
                        }}
                      />
                    )}
                  </div>

                  {/* Annotated Vehicle Image */}
                  <div className="col-span-2">
                    <h3 className="text-xs font-medium text-gray-700 mb-1">Annotated Vehicle</h3>
                    {loading ? (
                      <div className="w-full h-32 flex items-center justify-center bg-gray-100 rounded-lg">
                        <Loader className="animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <img
                        src={
                          details.annotatedVehicleImage ||
                          "https://via.placeholder.com/300x200?text=No+Annotated+Vehicle"
                        }
                        alt="Annotated vehicle"
                        className="w-full h-32 object-cover rounded-lg shadow cursor-pointer"
                        onClick={() => handleImageClick(details.annotatedVehicleImage || undefined)}
                        onError={(e) => {
                          console.log("Annotated vehicle image failed to load:", e.currentTarget.src);
                          e.currentTarget.src = "https://via.placeholder.com/300x200?text=No+Annotated+Vehicle";
                        }}
                      />
                    )}
                  </div>

                  {/* Annotated Plate Image */}
                  <div className="col-span-2">
                    <h3 className="text-xs font-medium text-gray-700 mb-1">Annotated Plate</h3>
                    {loading ? (
                      <div className="w-full h-32 flex items-center justify-center bg-gray-100 rounded-lg">
                        <Loader className="animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <img
                        src={
                          details.annotatedPlateImage ||
                          "https://via.placeholder.com/300x200?text=No+Annotated+Plate"
                        }
                        alt="Annotated vehicle"
                        className="w-full h-32 object-cover rounded-lg shadow cursor-pointer"
                        onClick={() => handleImageClick(details.annotatedPlateImage || undefined)}
                        onError={(e) => {
                          console.log("Annotated plate image failed to load:", e.currentTarget.src);
                          e.currentTarget.src = "https://via.placeholder.com/300x200?text=No+Annotated+Plat";
                        }}
                      />
                    )}
                  </div>

                  {/* Grid for smaller images - 2x2 layout */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Cropped Vehicle Image */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-700 mb-1">Cropped Vehicle</h3>
                      {loading ? (
                        <div className="w-full h-24 flex items-center justify-center bg-gray-100 rounded-lg">
                          <Loader className="animate-spin text-gray-400" size={16} />
                        </div>
                      ) : (
                        <img
                          src={
                            details.croppedVehicleImage ||
                            "https://via.placeholder.com/150x80?text=No+Vehicle+Image"
                          }
                          alt="Cropped vehicle"
                          className="w-full h-24 object-contain rounded-lg shadow cursor-pointer"
                          onClick={() => handleImageClick(details.croppedVehicleImage || undefined)}
                          onError={(e) => {
                            console.log("Cropped vehicle image failed to load:", e.currentTarget.src);
                            e.currentTarget.src = "https://via.placeholder.com/150x80?text=No+Vehicle+Image";
                          }}
                        />
                      )}
                    </div>

                    {/* Cropped Plate Image */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-700 mb-1">Cropped Plate</h3>
                      {loading ? (
                        <div className="w-full h-24 flex items-center justify-center bg-gray-100 rounded-lg">
                          <Loader className="animate-spin text-gray-400" size={16} />
                        </div>
                      ) : (
                        <img
                          src={
                            details.croppedPlateImage ||
                            "https://via.placeholder.com/150x80?text=No+Plate+Image"
                          }
                          alt="Cropped license plate"
                          className="w-full h-24 object-contain rounded-lg shadow cursor-pointer"
                          onClick={() => handleImageClick(details.croppedPlateImage || undefined)}
                          onError={(e) => {
                            console.log("Cropped plate image failed to load:", e.currentTarget.src);
                            e.currentTarget.src = "https://via.placeholder.com/150x80?text=No+Plate+Image";
                          }}
                        />
                      )}
                    </div>

                  </div>
                </>
              )}
            </div>

            {/* Right: Details - No changes needed */}
            <div className="space-y-2">
              {/* Detection Time - Read only, belongs to session */}
              <div className="flex justify-between">
                <span className="text-gray-600">Detection Time:</span>
                <span className="font-medium">
                  {details.timestamp || "N/A"}
                </span>
              </div>

              {/* Vehicle Type - Vehicle entity */}
              <div className="flex justify-between">
                <span className="text-gray-600">Vehicle Type:</span>
                {editMode ? (
                  <select
                    name="vehicleType"
                    value={form.vehicle.vehicleType}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-48"
                  >
                    <option value="">-- Select Vehicle Type --</option>
                    <option value="car">Car</option>
                    <option value="motorcycle">Motorcycle</option>
                  </select>
                ) : (
                  <span className="font-medium">{plateDetails?.vehicle_class || record.vehicleType}</span>
                )}
              </div>

              {/* Plate Number - Plate entity */}
              <div className="flex justify-between">
                <span className="text-gray-600">Plate Number:</span>
                {editMode ? (
                  <input
                    type="text"
                    name="plateNumber"
                    value={form.plate.plateNumber}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-32"
                  />
                ) : (
                  <span className="font-medium">{plateDetails?.plate_text || record.plateNumber}</span>
                )}
              </div>

              {/* Plate Date - Plate entity */}
              <div className="flex justify-between">
                <span className="text-gray-600">Plate Date:</span>
                {editMode ? (
                  <input
                    type="text"
                    name="plateDate"
                    value={form.plate.plateDate}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-32"
                    placeholder="MM/YY"
                  />
                ) : (
                  <span className="font-medium">{getDisplayValue("plate_date")}</span>
                )}
              </div>

              {/* Plate Type - Plate entity */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Plate Type:</span>
                {editMode ? (
                  <div className="flex gap-2">
                    <select
                      name="plateType"
                      value={form.plate.plateType}
                      onChange={handleChange}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="private">Private</option>
                      <option value="public">Public</option>
                      <option value="government">Government</option>
                      <option value="police">Police</option>
                      <option value="military">Military</option>
                      <option value="diplomatic">Diplomatic</option>
                      <option value="free_trade">Free Trade</option>
                    </select>
                    <select
                      name="plateStatus"
                      value={form.plate.plateStatus}
                      onChange={handleChange}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="whitelist">Whitelist</option>
                      <option value="blacklist">Blacklist</option>
                      <option value="unclassified">Unclassified</option>
                    </select>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getPlateTypeColor(
                        plateDetails?.plate_type || record.plateType
                      )}`}
                    >
                      {plateDetails?.plate_type || record.plateType}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getPlateTypeColor(
                        plateDetails?.plate_status || record.plateStatus
                      )}`}
                    >
                      {plateDetails?.plate_status || record.plateStatus || "unclassified"}
                    </span>
                  </div>
                )}
              </div>

              {/* Plate Colors - Plate entity */}
              <div className="flex justify-between">
                <span className="text-gray-600">Plate Text Color:</span>
                {editMode ? (
                  <input
                    type="text"
                    name="plateTextColor"
                    value={form.plate.plateTextColor}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-32"
                  />
                ) : (
                  <span className="font-medium">{getDisplayValue("plate_text_color")}</span>
                )}
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Plate Background Color:</span>
                {editMode ? (
                  <input
                    type="text"
                    name="plateBackgroundColor"
                    value={form.plate.plateBackgroundColor}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-32"
                  />
                ) : (
                  <span className="font-medium">{getDisplayValue("plate_background_color")}</span>
                )}
              </div>

              {/* Plate Region - Plate entity */}
              <div className="flex justify-between">
                <span className="text-gray-600">Plate Region:</span>
                {editMode ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      name="plateRegionCode"
                      value={form.plate.plateRegionCode}
                      onChange={handleChange}
                      className="border rounded px-2 py-1 text-sm"
                      placeholder="Region Code"
                    />
                    <input
                      type="text"
                      name="plateRegionName"
                      value={form.plate.plateRegionName}
                      onChange={handleChange}
                      className="border rounded px-2 py-1 text-sm"
                      placeholder="Region Name"
                    />
                  </div>
                ) : (
                  <span className="font-medium">
                    {plateDetails?.plate_region || record.region || "N/A"}
                  </span>
                )}
              </div>

              <hr className="my-2" />

              {/* Session data section - Watch only */}
              {/* Processing Time - Session entity, read only */}
              <div className="flex justify-between">
                <span className="text-gray-600">Processing Time:</span>
                <span className="font-medium">
                  {(() => {
                    const rawTime = record.processing_time ||
                      record.detection_details?.processing_time;

                    if (rawTime === null || rawTime === undefined) {
                      return "N/A";
                    }

                    if (typeof rawTime === 'string' &&
                      (rawTime.includes('ms') || rawTime.includes('s') || rawTime === "N/A")) {
                      return rawTime;
                    }

                    try {
                      const timeValue = parseFloat(rawTime);
                      if (!isNaN(timeValue)) {
                        return `${timeValue.toFixed(2)}ms`;
                      }
                    } catch (e) { }

                    return rawTime;
                  })()}
                </span>
              </div>

              {/* Image Resolution - Session entity, editable */}
              <div className="flex justify-between">
                <span className="text-gray-600">Image Resolution:</span>
                {editMode ? (
                  <input
                    type="text"
                    name="session.imageResolution" // Updated naming convention
                    value={form.session.imageResolution}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-32"
                  />
                ) : (
                  <span className="font-medium">
                    {record.image_resolution || record.detection_details?.image_resolution || "N/A"}
                  </span>
                )}
              </div>

              {/* Location - Session entity, editable with dropdown */}
              <div className="flex justify-between">
                <span className="text-gray-600">Location:</span>
                {editMode ? (
                  <select
                    name="session.location"
                    value={form.session.location}
                    onChange={handleChange}
                    className="border rounded px-2 py-1 text-sm w-full"
                  >
                    <option value="">-- Select Location --</option>
                    {availableLocations.map(loc => (
                      <option key={loc.id} value={loc.name}>
                        {loc.name} ({loc.type})
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="font-medium">
                    {(() => {
                      // Check multiple possible sources for location data
                      const locationValue =
                        plateDetails?.location ||
                        record.detection_details?.location ||
                        record.gateLocation ||
                        form.session.location;

                      // Return the location if available, otherwise N/A
                      return locationValue && locationValue !== "undefined" ? locationValue : "N/A";
                    })()}
                  </span>
                )}
              </div>

              {/* Algorithm - From detection_details, read-only */}
              <div className="flex justify-between">
                <span className="text-gray-600">Algorithm:</span>
                <span className="font-medium">{record.detection_details?.algorithm || "YOLOv11"}</span>
              </div>

              {/* Confidence - From plate details or record, read-only */}
              <div className="flex justify-between">
                <span className="text-gray-600">Confidence:</span>
                <span
                  className={`font-medium ${getConfidenceColor(
                    plateDetails?.ocr_confidence || record.confidence
                  )}`}
                >
                  {typeof (plateDetails?.ocr_confidence || record.confidence) === "number"
                    ? ((plateDetails?.ocr_confidence || record.confidence) * 100).toFixed(1) + "%"
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer section with buttons - fixed at bottom */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-2">
            {editMode ? (
              <>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition flex items-center gap-2 disabled:bg-blue-300"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving && <Loader size={16} className="animate-spin" />}
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                  onClick={() => setEditMode(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                onClick={() => setEditMode(true)}
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <style>
          {`
            @keyframes popIn {
              0% { opacity: 0; transform: scale(0.8);}
              100% { opacity: 1; transform: scale(1);}
            }
            .animate-pop-in {
              animation: popIn 0.2s cubic-bezier(0.4,0,0.2,1);
            }
          `}
        </style>

        {/* Image Preview Modal */}
        <ImagePreviewModal
          isOpen={showPreview}
          imageUrl={previewImage}
          onClose={handleClosePreview}
        />
      </div>
    </div>
  );
};

export default EditDetailModal;
